import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import meats from '../assets/images/logo/test.jpg';
import { About } from '../About/About';
import { Services } from '../About/Services';
import { Products } from '../Products/Products';
import { t } from 'i18next';

// Shared easing/timing so the whole entrance sequence feels like one motion,
// not several unrelated animations firing at once.
const EASE = [0.16, 1, 0.3, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 24, filter: 'blur(6px)' },
  show: (delay = 0) => ({
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.65, delay, ease: EASE },
  }),
};

// Slow ambient drift + a couple of custom keyframes Tailwind's default
// theme doesn't ship (float, shine sweep, gradient pan). Scoped with a
// unique wrapper class so it can't leak into/collide with other pages.
const heroMotionStyles = `
  .khairat-hero { position: relative; }

  .khairat-hero .drift-a { animation: khairatFloatA 11s ease-in-out infinite; }
  .khairat-hero .drift-b { animation: khairatFloatB 14s ease-in-out infinite; }
  .khairat-hero .spin-slow { animation: khairatSpin 26s linear infinite; }
  .khairat-hero .gradient-pan {
    background-size: 200% 200%;
    animation: khairatGradientPan 10s ease-in-out infinite;
  }
  .khairat-hero .cta-shine::before {
    content: '';
    position: absolute;
    inset-block: 0;
    inline-size: 40%;
    background: linear-gradient(115deg, transparent, rgba(255,255,255,0.35), transparent);
    transform: translateX(-160%) skewX(-15deg);
    transition: transform 0.6s ease;
  }
  .khairat-hero .cta-shine:hover::before { transform: translateX(360%) skewX(-15deg); }

  @keyframes khairatFloatA {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(10px, -18px); }
  }
  @keyframes khairatFloatB {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(-14px, 14px); }
  }
  @keyframes khairatSpin {
    to { transform: rotate(360deg); }
  }
  @keyframes khairatGradientPan {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  @media (prefers-reduced-motion: reduce) {
    .khairat-hero .drift-a,
    .khairat-hero .drift-b,
    .khairat-hero .spin-slow,
    .khairat-hero .gradient-pan { animation: none; }
  }
`;

// NEW clean, formal, modern homepage design
export const HomePage = () => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const isLatin = language === 'en' || language === 'en-us';
  const dir = isLatin ? 'ltr' : 'rtl';
  const prefersReducedMotion = useReducedMotion();

  // When reduced motion is requested, render everything already in its
  // final state instead of animating into place.
  const initial = prefersReducedMotion ? 'show' : 'hidden';

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  // Gentle parallax as the hero scrolls out of view — image drifts and
  // fades a touch slower than the page, background shapes drift opposite.
  const imageY = useTransform(scrollYProgress, [0, 1], [0, prefersReducedMotion ? 0 : -60]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, prefersReducedMotion ? 0 : 40]);

  // Subtle mouse-tilt on the portrait for a bit of depth on desktop.
  const tiltRef = useRef(null);
  const handleTilt = (e) => {
    if (prefersReducedMotion || !tiltRef.current) return;
    const rect = tiltRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    tiltRef.current.style.transform = `rotateY(${px * 10}deg) rotateX(${-py * 10}deg)`;
  };
  const resetTilt = () => {
    if (tiltRef.current) tiltRef.current.style.transform = 'rotateY(0deg) rotateX(0deg)';
  };

  const scrollToNext = () => {
    heroRef.current?.nextElementSibling?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <div className="w-full overflow-x-hidden">
      <style>{heroMotionStyles}</style>

      {/* HERO SECTION */}
      <section
        ref={heroRef}
        dir={dir}
        className="khairat-hero relative grid w-full min-h-screen grid-cols-1 items-center gap-10 overflow-hidden bg-white px-6 py-16 md:grid-cols-2 md:px-16"
      >
        {/* Decorative background accents — purely visual, sit behind content */}
        <motion.div
          aria-hidden="true"
          style={{ y: bgY }}
          className="pointer-events-none absolute inset-0 -z-10"
        >
          <div className="drift-a absolute -top-24 start-[-6rem] h-72 w-72 rounded-full bg-[#1a0080]/5 blur-3xl" />
          <div className="drift-b absolute bottom-[-4rem] end-[-4rem] h-80 w-80 rounded-full bg-[#c2450b]/10 blur-3xl" />
          <div className="gradient-pan absolute inset-0 bg-[linear-gradient(120deg,rgba(26,0,128,0.05),rgba(194,69,11,0.06),rgba(26,0,128,0.05))] opacity-70" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(26,0,128,0.06)_1px,transparent_0)] bg-[size:28px_28px] opacity-40" />
        </motion.div>

        {/* TEXT SIDE — first in reading order for both directions, mirrors visually via dir + logical order utilities */}
        <div
          className={`order-2 flex flex-col gap-6 ${
            dir === 'rtl' ? 'md:order-2 text-right' : 'md:order-1 text-left'
          }`}
        >
          <motion.h1
            variants={fadeUp}
            initial={initial}
            animate="show"
            custom={0}
            className="text-4xl font-extrabold leading-tight text-[#1a0080] md:text-6xl"
          >
            {t('homePage.heading1')}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial={initial}
            animate="show"
            custom={0.12}
            className="max-w-md text-lg text-gray-700 md:text-xl"
          >
            {t('homePage.heading2')}
          </motion.p>

          {/* Highlight Card */}
          <motion.div
            variants={fadeUp}
            initial={initial}
            animate="show"
            custom={0.24}
            whileHover={prefersReducedMotion ? undefined : { y: -4 }}
            className="relative overflow-hidden rounded-2xl border border-[#1a0080]/20 bg-[#f7f5ff] p-6 shadow-md transition-shadow duration-300 hover:shadow-xl"
          >
            <span
              aria-hidden="true"
              className="spin-slow pointer-events-none absolute -top-6 end-[-1.5rem] h-16 w-16 rounded-full border border-dashed border-[#1a0080]/25"
            />
            <p className="mb-3 text-2xl font-semibold text-[#1a0080]">
              {t('homePage.title')}
            </p>
            <p className="text-base leading-relaxed text-gray-700">
              {t('homePage.content')}
            </p>
          </motion.div>

          {/* CTA BUTTON */}
          <motion.div variants={fadeUp} initial={initial} animate="show" custom={0.36}>
            <Link
              to="/about"
              onClick={() => window.scrollTo(0, 0)}
              className="cta-shine group relative mt-4 inline-flex items-center gap-2 overflow-hidden rounded-xl bg-[#c2450b] px-6 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#150066] hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a0080]"
            >
              {t('homePage.button')}
              <span
                aria-hidden="true"
                className={`inline-block transition-transform duration-300 group-hover:translate-x-1 ${
                  dir === 'rtl' ? 'rotate-180 group-hover:-translate-x-1' : ''
                }`}
              >
                →
              </span>
            </Link>
          </motion.div>
        </div>

        {/* IMAGE SIDE */}
        <motion.div
          style={{ y: imageY }}
          className={`relative order-1 flex justify-center ${
            dir === 'rtl' ? 'md:order-1' : 'md:order-2'
          }`}
        >
          {/* Slowly rotating dashed ring accent behind the portrait, offset for depth */}
          <div
            aria-hidden="true"
            className="spin-slow absolute h-[240px] w-[240px] rounded-full border border-dashed border-[#1a0080]/20 md:h-[380px] md:w-[380px]"
          />
          <div
            aria-hidden="true"
            className="absolute h-[210px] w-[210px] rounded-full border border-[#1a0080]/15 md:h-[340px] md:w-[340px]"
          />

          <div style={{ perspective: '900px' }}>
            <motion.div
              ref={tiltRef}
              onMouseMove={handleTilt}
              onMouseLeave={resetTilt}
              initial={prefersReducedMotion ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: prefersReducedMotion ? 0 : 0.2, ease: EASE }}
              className="relative h-[260px] w-[260px] overflow-hidden rounded-full border-4 border-[#1a0080] shadow-2xl transition-transform duration-300 ease-out will-change-transform md:h-[420px] md:w-[420px]"
            >
              <img
                src={meats}
                alt="hero-img"
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll cue — icon only, no hardcoded copy, so it doesn't need its own translation key */}
        <motion.button
          type="button"
          onClick={scrollToNext}
          aria-label="Scroll to next section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: prefersReducedMotion ? 0 : 0.9, duration: 0.6 }}
          className={`absolute start-1/2 bottom-6 hidden items-center justify-center rounded-full border border-[#1a0080]/20 p-2 text-[#1a0080]/70 transition-colors duration-300 hover:border-[#1a0080]/40 hover:text-[#1a0080] md:flex ${
            dir === 'rtl' ? 'translate-x-1/2' : '-translate-x-1/2'
          }`}
        >
          <motion.span
            aria-hidden="true"
            animate={prefersReducedMotion ? {} : { y: [0, 6, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            className="text-lg leading-none"
          >
            ↓
          </motion.span>
        </motion.button>
      </section>

      {/* OTHER SECTIONS */}
      <About />
      <Services />
      <Products />
    </div>
  );
};